import { CommonModule,DatePipe,formatNumber } from '@angular/common';
import { Component,Input } from '@angular/core';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Store } from '@ngrx/store';
import moment from 'moment/moment';
import { BehaviorSubject,combineLatest,Observable,of,switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { State } from '../../../../../dave-data-module/State';
import { getWorkDayDictionary } from '../../../../../dave-data-module/State/selectors/work-day.selectors';
import { getWorkedTimes } from '../../../../../dave-data-module/State/selectors/worked-times.selectors';
import { DaveLoadingModule } from '../../../../../dave-loading/dave-loading.module';
import { AppDialogService } from '../../../../../dave-utils-module/app-dialog-module/app-dialog.service';
import { sameDay } from '../../../../../helper/helper';
import { CalendarPageMeta,CommissionMeta,ConstructionDiaryIcon,UserAdministrationMeta } from '../../../../../helper/page-metadata';
import { ResolveData } from '@angular/router';
import {
    ConstructionDiaryByCommissionIdResolverWithoutRoute,
} from '../../../../../dave-data-module/guards/construction-diary.resolver';
import { CONSTRUCTION_DIARY_KEY } from '../../../../../dave-data-module/State/reducers/construction-diary.reducer';
import { WorkedTimesResolver } from '../../../../../dave-data-module/guards/worked-times.resolver';
import { workedTimesFeatureKey } from '../../../../../dave-data-module/State/reducers/worked-times.reducer';
import { WorkDayResolver } from '../../../../../dave-data-module/guards/work-day.resolver';
import { workDaysFeatureKey } from '../../../../../dave-data-module/State/reducers/work-day.reducer';
import { HourList2Component, HourListComponentDialogData } from '../../../../../dave-time-tracker/components/hour-list-2/hour-list-2.component';
import { ConstructionDiaryEntity } from '../../../../../dave-data-module/entities/construction-diary.entity';
import {
    getConstructionDiariesByCommissionId, getConstructionDiaryFetchedByCommissionId,
} from '../../../../../dave-data-module/State/selectors/construction-diary.selectors';
import { getFetched$ } from '../../../../../dave-data-module/helper/helper';
const dayCount = 7;

@Component({
    selector: 'app-commission-scheduler',
    standalone: true,
    imports: [CommonModule, FontAwesomeModule, MatButtonModule, DaveLoadingModule, MatTooltipModule],
    templateUrl: './commission-scheduler.component.html',
    styleUrls: ['./commission-scheduler.component.scss'],
    providers: [DatePipe],
})
export class CommissionSchedulerComponent {
    public static readonly RequiredResolvers: ResolveData = {
        [workedTimesFeatureKey]: WorkedTimesResolver,
        [workDaysFeatureKey]: WorkDayResolver,
    };
    protected commissionId$ = new BehaviorSubject<number>(null);
    @Input() set commissionId(v: number) {
        if (this.commissionId$.value !== v) {
            this.commissionId$.next(v);
            getFetched$(
                this.store,
                getConstructionDiaryFetchedByCommissionId(v),
                getConstructionDiariesByCommissionId(v),
                { resolve: () => ConstructionDiaryByCommissionIdResolverWithoutRoute(this.store, v).subscribe() },
            )
        }
    }

    public DayCount = dayCount;
    public ToWeek = moment().isoWeek();
    public FromWeek = moment().add(-dayCount, 'day').isoWeek();

    public BtgbTable$: Observable<{
        columns: { text?: string; suffixText?: string; tooltip?: string; colorDot?: string; subLine?: string; onClick?: () => void }[][];
        rows: { icon: IconProp; theme: string; label: string; onClick: () => void }[];
    }> = this.commissionId$.pipe(
        switchMap((cId) =>
            combineLatest([
                this.store.select(getConstructionDiariesByCommissionId(cId)),
                this.store.select(getWorkedTimes).pipe(map((wt) => wt.filter((w) => w.CommissionId === cId))),
                this.store.select(getWorkDayDictionary),
            ]),
        ),
        map(([diaries, workedTimes, workdays]) => {
            const now = moment();
            const columns: { text?: string; suffixText?: string; tooltip?: string; colorDot?: string; subLine?: string; onClick?: () => void }[][] = [];
            for (let i = 0; i < dayCount; i++) {
                const day = now.clone().add(-i, 'days').toDate();
                const diary = diaries.find((d) => sameDay(day, d.Date));

                const timespan = workedTimes.filter((wt) => workdays[wt.WorkDayId] && sameDay(day, workdays[wt.WorkDayId].Timestamp)).reduce((prev, wt) => prev + wt.Timespan, 0) / 1000 / 60 / 60;
                columns.unshift([
                    { subLine: this.datePipe.transform(day, 'd.M'), text: this.datePipe.transform(day, 'EE') },
                    {
                        colorDot: diary?.getStatusColor() || 'red',
                        tooltip: diary?.getStatusDescription() || 'kein Bautagebuch',
                        onClick: () => this.dialogService.OpenDetailConstructionDiaryPopup(moment(day), this.commissionId$.value),
                    },
                    {
                        text: formatNumber(timespan, 'de-DE', '1.0-2'),
                        suffixText: ' Std.',
                    },
                ]);
            }
            return {
                columns,
                rows: [
                    {
                        icon: CalendarPageMeta.Icon,
                        theme: CalendarPageMeta.Theme,
                        label: 'Kalender',
                        onClick: () => {},
                    },
                    {
                        icon: ConstructionDiaryIcon,
                        theme: CommissionMeta.Theme,
                        label: 'Bautagebuch',
                        onClick: () => this.dialogService.OpenConstructionDiaryDatepickerDialog(this.commissionId$.value),
                    },
                    {
                        icon: 'stopwatch' as IconProp,
                        theme: UserAdministrationMeta.Theme,
                        label: 'Zeiten',
                        onClick: () => this.OpenTimeTracker(),
                    },
                ],
            };
        }),
    );
    constructor(private store: Store<State>, private datePipe: DatePipe, private dialog: MatDialog, public dialogService: AppDialogService) {}

    public OpenTimeTracker() {
        this.dialog.open<HourList2Component, HourListComponentDialogData>(HourList2Component, {
            ...HourList2Component.DefaultConfig,
            data: {
                CommissionId: this.commissionId$.value,
            },
        });
    }
}
